import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';


const QuickActionToolbar = () => {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  const getContextActions = () => {
    switch (location?.pathname) {
      case '/tracking-number-entry-validation':
        return [
          { 
            icon: 'Upload', 
            label: 'Bulk Import', 
            shortcut: 'Ctrl+I',
            action: () => console.log('Bulk import triggered')
          },
          { 
            icon: 'Copy', 
            label: 'Paste Numbers', 
            shortcut: 'Ctrl+V',
            action: () => console.log('Paste triggered')
          },
          { 
            icon: 'Trash2', 
            label: 'Clear All', 
            shortcut: 'Ctrl+Del',
            action: () => console.log('Clear all triggered')
          }
        ];
      case '/shipment-history-search':
        return [
          { 
            icon: 'Download', 
            label: 'Export Results', 
            shortcut: 'Ctrl+E',
            action: () => console.log('Export triggered')
          },
          { 
            icon: 'Filter', 
            label: 'Advanced Filter', 
            shortcut: 'Ctrl+F',
            action: () => console.log('Filter triggered')
          },
          { 
            icon: 'RefreshCw', 
            label: 'Refresh', 
            shortcut: 'F5',
            action: () => console.log('Refresh triggered')
          }
        ];
      case '/reporting-analytics-dashboard':
        return [
          { 
            icon: 'FileText', 
            label: 'Generate Report', 
            shortcut: 'Ctrl+G',
            action: () => console.log('Generate report triggered')
          },
          { 
            icon: 'Download', 
            label: 'Export Data', 
            shortcut: 'Ctrl+E',
            action: () => console.log('Export data triggered')
          },
          { 
            icon: 'Calendar', 
            label: 'Date Range', 
            shortcut: 'Ctrl+D',
            action: () => console.log('Date range triggered')
          }
        ];
      default:
        return [];
    }
  };

  const actions = getContextActions();

  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        actions?.forEach(action => {
          const key = action?.shortcut?.split('+')?.[1]?.toLowerCase();
          if (e?.key?.toLowerCase() === key) {
            e?.preventDefault();
            action?.action();
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [actions]);

  if (actions?.length === 0) {
    return null;
  }

  return (
    <>
      <div className="hidden md:block fixed bottom-32 right-32 z-200">
        <div className="flex flex-col gap-12 items-end">
          {isExpanded && (
            <div className="flex flex-col gap-12 bg-card rounded-lg shadow-elevation-4 p-12 border border-border animate-in slide-in-from-bottom-4 duration-200">
              {actions?.map((action, index) => (
                <button
                  key={index}
                  onClick={action?.action}
                  className="flex items-center gap-18 px-24 h-[48px] rounded-lg bg-background hover:bg-muted transition-smooth group"
                  title={`${action?.label} (${action?.shortcut})`}
                >
                  <Icon 
                    name={action?.icon} 
                    size={20} 
                    color="var(--color-foreground)" 
                    strokeWidth={2}
                    className="group-hover:scale-110 transition-smooth" 
                  />
                  <div className="flex flex-col items-start">
                    <span className="font-body font-medium text-[14px] text-foreground whitespace-nowrap">
                      {action?.label}
                    </span>
                    <span className="font-caption text-[11px] text-muted-foreground">
                      {action?.shortcut}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`
              w-[56px] h-[56px] rounded-full shadow-elevation-4
              flex items-center justify-center
              transition-smooth hover:scale-105 active:scale-95
              ${isExpanded ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'}
            `}
            aria-label={isExpanded ? 'Close quick actions' : 'Open quick actions'}
            aria-expanded={isExpanded}
          >
            <Icon 
              name={isExpanded ? 'X' : 'Zap'} 
              size={24} 
              color="currentColor" 
              strokeWidth={2}
              className={isExpanded ? '' : 'animate-pulse'} 
            />
          </button>
        </div>
      </div>
      <div className="md:hidden fixed bottom-24 right-24 z-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`
            w-[56px] h-[56px] rounded-full shadow-elevation-4
            flex items-center justify-center
            transition-smooth active:scale-95
            ${isExpanded ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'}
          `}
          aria-label="Quick actions menu"
        >
          <Icon 
            name={isExpanded ? 'X' : 'Zap'} 
            size={24} 
            color="currentColor" 
            strokeWidth={2} 
          />
        </button>

        {isExpanded && (
          <div className="fixed inset-0 bg-background z-200 overflow-y-auto animate-in slide-in-from-bottom duration-200">
            <div className="p-24">
              <div className="flex items-center justify-between mb-32">
                <h2 className="font-heading font-semibold text-h4 text-foreground">
                  Quick Actions
                </h2>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="w-[44px] h-[44px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
                  aria-label="Close quick actions"
                >
                  <Icon name="X" size={24} color="var(--color-foreground)" strokeWidth={2} />
                </button>
              </div>

              <div className="flex flex-col gap-12">
                {actions?.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      action?.action();
                      setIsExpanded(false);
                    }}
                    className="flex items-center gap-18 px-24 h-[64px] rounded-lg bg-card hover:bg-muted transition-smooth border border-border"
                  >
                    <div className="w-[44px] h-[44px] rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon 
                        name={action?.icon} 
                        size={22} 
                        color="var(--color-primary)" 
                        strokeWidth={2} 
                      />
                    </div>
                    <div className="flex-1 flex flex-col items-start">
                      <span className="font-body font-medium text-[16px] text-foreground">
                        {action?.label}
                      </span>
                      <span className="font-caption text-[13px] text-muted-foreground">
                        {action?.shortcut}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuickActionToolbar;